"use client";
import { emailValidation } from "@/app/model/login";
import React, { useState } from "react";
import { z } from "zod";
import ReferVoucher from "./refer-voucher";
import { Input } from "@/stories/input/input";
import Info from "@/app/assets/icons/info";
import { ToastContainer, toast } from "react-toastify";
import CopyToDashboard from "@/app/assets/icons/copy-to-dashboard";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import { referralTag } from "@/app/api/service/refer";
import { useRouter } from "next/navigation";
import ShareOptions from "./share-option";
import useMobile from "@/app/hooks/use-mobile";
import ReferStatusModal from "./refer-status-modal";
// import { initBranch, trackEvent } from "@/app/utils/branchio";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";

interface ReferComponentProps {
    isKycDone: boolean;
    refLink: string;
    username?: string;
    from?: string;
    forWeb?: boolean;
    referrerEmail?: string;
    userId?: string;
    phone?: string;
    recaptchaEnabled?: boolean;
    isLoading?: boolean;
    voucherAmt?: number;
    voucherText?: string;
    hideDashboardLink?: boolean;
    hideTransactionBenefit?: boolean;
}

const ReferComponent: React.FC<ReferComponentProps> = ({
    isKycDone,
    refLink,
    from,
    username,
    referrerEmail,
    userId,
    phone,
    recaptchaEnabled,
    isLoading,
    voucherAmt,
    voucherText,
    hideDashboardLink,
    hideTransactionBenefit,
}) => {
    const router = useRouter();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { isMobile } = useMobile();
    const { cleverTapTrackEvent } = useCleverTapEvents();

    const [userEmail, setuserEmail] = useState<string>("");
    const [emailError, setEmailError] = useState<string | null>(null); // Error for email
    const [shareLoading, setShareLoading] = useState(false);
    const [popOpen, setPopOpen] = useState(false);
    const [isShareOpen, setIsShareOpen] = useState<boolean>(false);
    const [referPopupContent, setReferPopupContent] = useState<{ type: "success" | "error"; message: string }>({
        type: "success",
        message: "",
    });

    const triggerEnterKeyUp = (e: React.KeyboardEvent<HTMLInputElement>, userEmail: string) => {
        if (String(e.key)?.toLowerCase() === "enter") {
            handleInvite(userEmail);
        }
    };

    const shareEmailInvite = (userEmail: string) => {
        try {
            emailValidation.parse(userEmail);
        } catch (error) {
            if (error instanceof z.ZodError) {
                error.errors.forEach((err) => {
                    if (err.message.toLowerCase().includes("email")) {
                        setEmailError(err.message); // Set error for email
                        throw new Error("Invalid email address"); // Stop further execution
                    }
                });
            }
        }
    };

    const handleInvite = (email: string) => {
        try {
            shareEmailInvite(email); // Validate email first
            inviteUser(email); // Proceed only if no error
            cleverTapTrackEvent("US_Ref_InviteClick", { email: referrerEmail, phone, userId });
            // initBranch()
            // .then(() => {
            //     trackEvent("US_Ref_InviteClick", referrerEmail, phone, userId);
            // })
            // .catch(error => console.error('Error initializing Branch:', error))
        
            setAnalyticsEvent(window.location.origin, "US_Ref_InviteClick");
        } catch (error) {
            console.error("Error inviting user:", error);
        }
    };

    function generateRecaptchToken(action: string): Promise<string> {
        return new Promise((resolve, reject) => {
            // If reCAPTCHA is disabled, return a dummy token
            if (!recaptchaEnabled) {
                console.log("reCAPTCHA is disabled, returning dummy token");
                resolve("");
                return;
            }
            
            if (typeof executeRecaptcha !== "function") {
                reject(new Error("Execute recaptcha not yet available"));
                return;
            }
            
            executeRecaptcha(action)
                .then((token: string) => {
                    resolve(token);
                })
                .catch((error: Error) => {
                    console.error("reCAPTCHA error:", error);
                    reject(error);
                });
        });
    }  

    const inviteUser = async (email: string) => {
        if (shareLoading) return; // Prevent multiple clicks
        const token = await generateRecaptchToken("referral");

        setShareLoading(true); // Start loader
        const payload = {
            refCode: refLink,
            referral: email,
            captchaToken: token ? token : ""
        };

        try {
            const response = await referralTag(payload);

            if (response?.success) {
                setReferPopupContent({
                    type: "success",
                    message: "Invitation sent successfully!",
                });
                // toast.success("Invitation sent successfully");
            } else {
                setReferPopupContent({
                    type: "error",
                    message: response?.message || "Something went wrong",
                });
                // toast.error(response?.message || "Something went wrong");
            }
        } catch (error) {
            setReferPopupContent({
                type: "error",
                message: "An error occurred. Please try again.",
            });
            console.error("An error occurred in referral: ", error);
        } finally {
            setPopOpen(true); // Show the popup
            setShareLoading(false); // Stop loader
        }
    };

    const toggleReferPopup = () => {
        setPopOpen(prev => !prev);
    }

    const handleCopyLink = async() => {
        await navigator.clipboard.writeText(refLink || "");
        toast.success("Copied successfully");
    }

    if(isLoading) {
        return (
            <div className="m-0 flex flex-col items-center justify-center md:my-14 md:w-[1152px] md:flex-row md:justify-between md:gap-[84px] md:px-[68px] animate-pulse">
                <div className="flex h-full w-full justify-end md:block">
                    <div className="h-40 w-72 bg-neutral-200 rounded-lg mb-6 md:mb-0" />
                </div>
                <div className="min-w-full md:min-h-[423px] md:min-w-[525px] md:rounded-[16px] md:bg-white md:shadow-lg">
                    <div className="mb-2 h-8 w-1/2 mx-auto bg-neutral-200 rounded" />
                    <div className="flex flex-col p-0 md:gap-6 md:p-8">
                        <div className="w-full">
                            <div className="mb-2 h-4 w-24 bg-neutral-200 rounded" />
                            <div className="flex h-14 items-center gap-1">
                                <div className="h-14 w-full md:w-4/5 bg-neutral-200 rounded-[12px]" />
                                <div className="hidden md:block h-14 w-1/5 bg-neutral-200 rounded-[12px]" />
                            </div>
                            <div className="md:hidden block min-h-14 w-full mt-6 bg-neutral-200 rounded-full" />
                        </div>
                        <div className="w-full mt-6">
                            <div className="mb-2 h-4 w-32 bg-neutral-200 rounded" />
                            <div className="flex h-14 items-center justify-between rounded-[12px] border border-neutralOpacityDark-10 p-s7 bg-neutral-100">
                                <div className="h-6 w-3/4 bg-neutral-200 rounded" />
                                <div className="h-6 w-6 bg-neutral-200 rounded" />
                            </div>
                        </div>
                        <div className="mb-8 mt-6 flex w-full flex-col items-center justify-center md:mb-0 md:mt-0 md:block">
                            <div className="h-14 w-full bg-neutral-200 rounded-full" />
                        </div>
                        <div className="mx-auto mt-8 flex w-1/3 items-center md:hidden">
                            <div className="flex-grow border-t border-dashed border-gray-200"></div>
                            <div className="mx-4 h-4 w-8 bg-neutral-200 rounded" />
                            <div className="flex-grow border-t border-dashed border-gray-200"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="m-0 flex flex-col items-center justify-center md:my-14 md:w-[1152px] md:flex-row md:justify-between md:gap-[84px] md:px-[68px]">
                <div
                    className={`flex h-full w-full justify-end md:block ${from === "fromDrawer" ? "hidden" : ""}`}
                >

                    <ReferVoucher voucherAmt={voucherAmt} voucherText={voucherText} hideTransactionBenefit={hideTransactionBenefit} />
                </div>
                <div
                    className={`min-w-full md:min-h-[423px] md:min-w-[525px] md:rounded-[16px] md:bg-white md:shadow-lg ${from === "fromDrawer" ? "block" : "hidden md:block"}`}
                >
                    <div className="mb-2 text-center text-3xl font-semibold leading-[31.2px] tracking-[-0.24px] md:mb-0 md:rounded-t-[16px] md:border-b md:border-b-neutralOpacityDark-10 md:px-s13 md:py-s9 md:text-3xl md:font-semibold md:leading-[31.2px] md:tracking-[-0.24px]">
                        Refer Now
                    </div>
                    {from === "fromDrawer" ? (
                        <div className="mb-6 pb-4 text-sm leading-[22.4px] tracking-[0.14px] text-neutral-500">
                            Share your unique referral link with
                            <br /> friends & start earning
                        </div>
                    ) : null}
                    <div className="flex flex-col p-0 md:gap-6 md:p-8">
                        <div className="w-full">
                            <p className="mb-2 text-left text-sm leading-[19.6px] text-neutral-300">
                                Enter mail id*
                            </p>
                            <div className="flex h-14 items-center justify-center gap-1">
                                <Input
                                    id="refer-email-input"
                                    placeholder="abc@gmail.com"
                                    showIcon={false}
                                    className="min-h-14 w-full md:w-4/5 rounded-[12px] border border-neutralOpacityDark-10 text-md leading-[22.4px] text-neutral-900"
                                    value={userEmail}
                                    disabled={!isKycDone}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setEmailError("");
                                        setuserEmail(val);
                                    }}
                                    onKeyUp={(e) => {
                                        triggerEnterKeyUp(e, userEmail);
                                    }}
                                />

                                <RegularButton
                                    noIcon
                                    showRightIcon={false}
                                    size={ButtonSize.Large}
                                    variant={ButtonVariant.SecondaryDefault}
                                    label="Invite"
                                    disabled={!userEmail || shareLoading}
                                    className="hidden md:block min-h-14 w-1/5 max-w-full rounded-[12px] border border-neutralOpacityDark-10 py-s7 text-md leading-[22.4px] text-neutral-900"
                                    onClick={() => handleInvite(userEmail)}
                                />
                            </div>
                            <RegularButton
                                noIcon
                                showRightIcon={false}
                                size={ButtonSize.Large}
                                variant={ButtonVariant.SecondaryDefault}
                                label="Invite"
                                disabled={!userEmail || shareLoading}
                                className="md:hidden block min-h-14 w-full max-w-full mt-6 rounded-full bg-neutral-50 border border-neutralOpacityDark-10 py-s7 text-md leading-[22.4px] text-neutral-900"
                                onClick={() => handleInvite(userEmail)}
                            />
                            <div
                                className={`flex w-full transform items-center justify-start gap-2 transition-all duration-500 ${
                                    emailError
                                        ? "mb-2 mt-2 translate-y-0 opacity-100"
                                        : "mb-0 mt-2 -translate-y-2 opacity-0"
                                }`}
                            >
                                <Info width="12" height="12" className="text-error-600" />
                                <p className="text-sm text-error-600">{emailError}</p>
                            </div>
                        </div>
                        <div className="w-full">
                            <p className="mb-2 text-left text-sm leading-[19.6px] text-neutral-300">
                                Share referral link
                            </p>
                            <div className="flex h-14 items-center justify-between rounded-[12px] border border-neutralOpacityDark-10 p-s7">
                                <p className="w-full text-ellipsis text-left text-md leading-[22.4px] text-neutral-900 max-w-[calc(100%-36px)] overflow-hidden whitespace-nowrap">
                                    {refLink || ""}
                                </p>
                                <div
                                    className="cursor-pointer"
                                    onClick={() => refLink ? handleCopyLink() : null}
                                >
                                    <CopyToDashboard />
                                </div>
                            </div>
                            <ReferStatusModal 
                                type={referPopupContent.type}
                                message={referPopupContent.message}
                                isOpen={popOpen}
                                onClose={() => toggleReferPopup()}
                            />
                        </div>
                        {/* <div className="flex w-full flex-col items-center justify-center md:mt-0 mt-6 md:mb-0 mb-8">
                            <RegularButton
                                noIcon
                                showRightIcon={false}
                                size={ButtonSize.Large}
                                variant={ButtonVariant.PrimaryDefault}
                                label="Share Invite"
                                disabled={!userEmail}
                                className="w-full max-w-full"
                                onClick={() => handleInvite(userEmail)}
                            />
                        </div> */}
                        <div className="mb-8 mt-6 flex w-full flex-col items-center justify-center md:mb-0 md:mt-0 md:block">
                            <RegularButton
                                noIcon
                                showRightIcon={false}
                                size={ButtonSize.Large}
                                disabled={!refLink}
                                variant={ButtonVariant.PrimaryDefault}
                                label="Share Invite"
                                className="w-full max-w-full"
                                onClick={() => setIsShareOpen(true)}
                            />
                        </div>
                        <div className="mx-auto mt-8 flex w-1/3 items-center md:hidden">
                            <div className="flex-grow border-t border-dashed border-gray-300"></div>
                            <div className="mx-4 text-sm font-medium uppercase text-gray-500">
                                OR
                            </div>
                            <div className="flex-grow border-t border-dashed border-gray-300"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="relative">
                {from === "fromDrawer" || !isMobile ? (
                    <ShareOptions
                        refLink={refLink}
                        isOpen={isShareOpen}
                        onClose={() => setIsShareOpen(false)}
                        username={username}
                        email={referrerEmail}
                        userId={userId}
                        phone={phone}
                        handleCopyLink={handleCopyLink}
                    />
                ) : null}

                {from === "fromDrawer" || hideDashboardLink ? null : (
                    <div className="mt-[17px] flex w-full justify-center">
                        <p
                            className="max-w-max cursor-pointer text-lg font-medium leading-[29.2px] tracking-[-0.18px] underline"
                            onClick={() => router.push("/refer-dashboard")}
                        >
                            View Referral Dashboard
                        </p>
                    </div>
                )}
            </div>
            <ToastContainer />
        </>
    );
};

export default ReferComponent;
